import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { useWorkspace, Team, Project } from '../../context/WorkspaceContext';
import { useNotification } from '../../context/NotificationContext';
import UserProfile from '../UserProfile/UserProfile';
import NavMenu from '../NavMenu/NavMenu';
import { IconTextButton } from '../Button';
import CreateProjectDialog from '../CreateProjectDialog/CreateProjectDialog';
import './TopNavBar.css';
// @ts-ignore
import logoImage from '../../image-assets/logo.svg';
// @ts-ignore
import dropdownIcon from '../../image-assets/icon.arrows.dropdown.svg';
// @ts-ignore
import arrowLeftIcon from '../../image-assets/icon.arrow.left.svg';
// @ts-ignore
import checkmarkIcon from '../../image-assets/icon.checkmark.svg';
// @ts-ignore
import editIcon from '../../image-assets/icon.edit.svg';
import ProjectDropdown from '../ProjectDropdown/ProjectDropdown';
// @ts-ignore
import addIcon from '../../image-assets/icon.add.svg';
// @ts-ignore
import searchIcon from '../../image-assets/icon.search.svg';
import { TextButton } from '../Button';
import { useDialog } from '../../context/DialogContext';
import EditProjectDialog from '../EditProjectDialog/EditProjectDialog';
import CreateTeamDialog from '../CreateTeamDialog/CreateTeamDialog';
import { usePermissions } from '../../lib/permissions';
import { useSubscription } from '../../hooks/useSubscription';
import SubscriptionDialog from '../SubscriptionDialog/SubscriptionDialog';

interface TopNavBarProps {
  onBackClickProp?: () => void;
  reviewTitle?: string;
  onEditClick?: () => void;
  onCompleteClick?: () => void;
  isReviewDetailViewProp?: boolean;
  reviewId?: string;
}

// Interface for the custom event detail
interface NavBarStateUpdate {
  reviewTitle?: string;
  onBackClick?: () => void;
  isReviewCompleted?: boolean;
  onEditClick?: () => void;
  onCompleteClick?: () => void;
  isReviewDetailView?: boolean;
  reviewId?: string;
}

const TopNavBar: React.FC<TopNavBarProps> = ({ 
  onBackClickProp,
  reviewTitle: propReviewTitle,
  onEditClick: propOnEditClick,
  onCompleteClick: propOnCompleteClick,
  isReviewDetailViewProp = false,
  reviewId
}) => {
  useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { isAnyDialogOpen } = useDialog();
  const { user } = useAuth();
  const { profileData } = useProfile();
  const { 
    teams, 
    projects, 
    selectedTeam, 
    selectedProject,
    setSelectedTeam,
    setSelectedProject,
    createProject,
    updateProject,
    deleteProject,
    createTeam,
    isLoading
  } = useWorkspace();
  const { permissions } = usePermissions(selectedTeam, user?.id, profileData?.id);
  const { hasActiveSubscription, isLoading: isSubscriptionLoading } = useSubscription();
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);

  // State for the TopNavBar
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCreateProjectDialogOpen, setIsCreateProjectDialogOpen] = useState(false);
  const [isEditProjectDialogOpen, setIsEditProjectDialogOpen] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null);
  const [creatingProject, setCreatingProject] = useState(false);
  const [editingProject, setEditingProject] = useState(false);
  const [, setCreateProjectTeamId] = useState<string | null>(null);
  const [isCreateTeamDialogOpen, setIsCreateTeamDialogOpen] = useState(false);
  const [creatingTeam, setCreatingTeam] = useState(false);
  
  // State for review detail view (can be set by props or custom event)
  const [isReviewDetailView, setIsReviewDetailView] = useState(isReviewDetailViewProp);
  const [reviewTitle, setReviewTitle] = useState(propReviewTitle);
  const [isReviewCompleted, setIsReviewCompleted] = useState(false);
  const [onBackClick, setOnBackClick] = useState<(() => void) | undefined>(onBackClickProp);
  const [, setOnEditClick] = useState<(() => void) | undefined>(propOnEditClick);
  const [onCompleteClick, setOnCompleteClick] = useState<(() => void) | undefined>(propOnCompleteClick);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentReviewId, setCurrentReviewId] = useState(reviewId);
  
  // Check if current route is part of onboarding
  const isOnboardingRoute = ['/login', '/signup', '/verify', '/user-info', '/create-team'].includes(location.pathname);
  
  // Check if current user is team owner
  const isTeamOwner = selectedTeam?.owner_profile_id === profileData?.id;
  
  // CRITICAL: Always update isReviewDetailView based on URL path
  // This ensures the UI state is always in sync with the actual route
  useEffect(() => {
    const isReviewPath = location.pathname.includes('/review/');

    // Update the review detail view state without animations
    setIsReviewDetailView(isReviewPath);

    // Close dropdown when navigating to a different page
    setIsDropdownOpen(false);
  }, [location.pathname, isReviewDetailView]);
  
  // Listen for custom events from ReviewDetail
  useEffect(() => {
    const handleNavBarUpdate = (event: CustomEvent<NavBarStateUpdate>) => {
      const { detail } = event;
      
      // Update state immediately without animations
      if (detail.isReviewDetailView !== undefined) {
        setIsReviewDetailView(detail.isReviewDetailView);
      }
      
      if (detail.reviewTitle !== undefined) {
        setReviewTitle(detail.reviewTitle);
      }
      
      if (detail.isReviewCompleted !== undefined) {
        setIsReviewCompleted(detail.isReviewCompleted);
      }
      
      if (detail.reviewId !== undefined) {
        setCurrentReviewId(detail.reviewId);
      }
      
      // Update callback functions
      if (detail.onBackClick !== undefined) {
        setOnBackClick(() => detail.onBackClick);
      }
      if (detail.onEditClick !== undefined) {
        setOnEditClick(() => detail.onEditClick);
      }
      if (detail.onCompleteClick !== undefined) {
        setOnCompleteClick(() => detail.onCompleteClick);
      }
    };
    
    // Handle create project dialog open event
    const handleOpenCreateProjectDialog = (event: CustomEvent<{ teamId: string }>) => {
      const { teamId } = event.detail;
      setCreateProjectTeamId(teamId);
      setIsCreateProjectDialogOpen(true);
    };
    
    // Add event listener for custom events
    window.addEventListener('update-top-nav', handleNavBarUpdate as EventListener);
    window.addEventListener('open-create-project-dialog', handleOpenCreateProjectDialog as EventListener);
    
    // Clean up event listener
    return () => {
      window.removeEventListener('update-top-nav', handleNavBarUpdate as EventListener);
      window.removeEventListener('open-create-project-dialog', handleOpenCreateProjectDialog as EventListener);
    };
  }, []);
  
  // Update from props when they change
  useEffect(() => {
    setReviewTitle(propReviewTitle);
    setIsReviewCompleted(false);
    setOnBackClick(() => onBackClickProp);
    setOnEditClick(() => propOnEditClick);
    setOnCompleteClick(() => propOnCompleteClick);
    setCurrentReviewId(reviewId);
  }, [onBackClickProp, propReviewTitle, propOnEditClick, propOnCompleteClick, reviewId]);

  // Close dropdown when any dialog opens
  useEffect(() => {
    if (isAnyDialogOpen) {
      setIsDropdownOpen(false);
    }
  }, [isAnyDialogOpen]);

  // Debug logging
  useEffect(() => {
    // Removed debug logging
  }, [location.pathname, selectedProject]);

  // Update toolbar buttons when team or project changes
  useEffect(() => {
    // Removed debug logging
  }, [selectedTeam, selectedProject, profileData, isTeamOwner, location.pathname]);

  // Update toolbar buttons when review changes
  useEffect(() => {
    // ... existing code ...
  }, [currentReviewId, selectedProject, selectedTeam, isTeamOwner]);

  // If we're on an onboarding route, don't render the nav bar
  if (isOnboardingRoute) {
    return null;
  }

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleSelectTeam = (team: Team) => {
    setSelectedTeam(team);
    setSelectedProject(null);
    setIsDropdownOpen(false);
  };

  const handleSelectProject = (project: Project) => {
    setSelectedProject(project);
    setIsDropdownOpen(false);
  };

  // Transform the teams and projects data into the format ProjectDropdown expects
  const dropdownItems = {
    teams: teams.map(team => ({
      id: team.id,
      name: team.name,
      projects: projects
        .filter(project => project.team_id === team.id)
        .map(project => ({
          id: project.id,
          name: project.name,
          placeholder_format: project.placeholder_format
        }))
    }))
  };

  const handleProjectSelect = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      const team = teams.find(t => t.id === project.team_id);
      if (team) {
        setSelectedTeam(team);
        handleSelectProject(project);
      }
    }
  };

  const getDisplayText = () => {
    if (selectedTeam && selectedProject) {
      return (
        <>
          {selectedTeam.name} 
          <span className="dropdown-separator">/</span> 
          {selectedProject.name}
        </>
      );
    }
    if (selectedTeam) {
      return selectedTeam.name;
    }
    return 'Select Team / Project';
  };

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate('/reviews');
    }
  };

  const handleEditProject = async (newName: string, placeholderFormat: string) => {
    if (!projectToEdit) return;
    try {
      setEditingProject(true);
      await updateProject(projectToEdit.id, newName, placeholderFormat);
      showNotification('Project updated');
      setIsEditProjectDialogOpen(false);
      setProjectToEdit(null);
    } catch (error) {
      showNotification('Failed to update project');
    } finally {
      setEditingProject(false);
    }
  };

  const handleCreateProject = async (name: string, placeholderFormat: string, languages: string[]) => {
    if (!selectedTeam) return;
    try {
      setCreatingProject(true);
      await createProject(selectedTeam.id, name, placeholderFormat, languages);
      showNotification('Project created successfully');
      setIsCreateProjectDialogOpen(false);
      navigate('/overview');
    } catch (error) {
      showNotification('Failed to create project');
    } finally {
      setCreatingProject(false);
    }
  };

  const handleProjectDelete = async () => {
    if (!projectToEdit) return;
    try {
      setEditingProject(true);
      await deleteProject(projectToEdit.id);
      showNotification('Project deleted');
      setIsEditProjectDialogOpen(false);
      setProjectToEdit(null);
    } catch (error) {
      showNotification('Failed to delete project');
    } finally {
      setEditingProject(false);
    }
  };

  const handleCreateTeam = async (name: string) => {
    try {
      setCreatingTeam(true);
      await createTeam(name);
      showNotification('Team created successfully');
      setIsCreateTeamDialogOpen(false);
      navigate('/overview');
    } catch (error) {
      showNotification('Failed to create team');
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleImportClick = () => {
    if (!selectedProject) {
      showNotification('No project selected');
      return;
    }
    // Import is subscription-only (block for free users regardless of key count)
    if (!isSubscriptionLoading && !hasActiveSubscription) {
      setIsSubscriptionDialogOpen(true);
      return;
    }
    const event = new CustomEvent('open-import-dialog', {
      detail: { projectId: selectedProject.id }
    });
    window.dispatchEvent(event);
  };

  const handleExportClick = () => {
    if (!selectedProject) {
      showNotification('No project selected');
      return;
    }
    
    // Dispatch event to open export dialog
    const event = new CustomEvent('open-export-dialog', {
      detail: { projectId: selectedProject.id }
    });
    window.dispatchEvent(event);
  };

  const handleProjectClick = (projectId: string, name: string) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setProjectToEdit(project);
      setIsEditProjectDialogOpen(true);
    }
  };

  return (
    <nav className={`top-nav-bar ${isReviewDetailView ? 'review-detail-nav' : ''}`}>
      <div className="main-nav">
        <div className="nav-left">
          <div className="nav-logo">
            <img src={logoImage} alt="Texta Logo" className="logo-image" />
          </div>

          {isReviewDetailView ? (
            <IconTextButton 
              variant="text"
              size="small"
              icon={arrowLeftIcon}
              iconAlt="Back"
              onClick={handleBackClick}
              className="nav-back-button"
            >
              All reviews
            </IconTextButton>
          ) : (
            <div className="nav-dropdown-container">
              <button 
                className="nav-dropdown-button" 
                onClick={toggleDropdown}
                disabled={isLoading || teams.length === 0}
              >
                <span>{getDisplayText()}</span>
                <img src={dropdownIcon} alt="Dropdown" className="dropdown-icon" />
              </button>
              
              <ProjectDropdown 
                isOpen={isDropdownOpen}
                items={dropdownItems}
                onSelect={handleProjectSelect}
                onTeamSelect={(teamId) => {
                  const team = teams.find(t => t.id === teamId);
                  if (team) {
                    handleSelectTeam(team);
                  }
                }}
                onCreateProject={(teamId) => {
                  setCreateProjectTeamId(teamId);
                  setIsCreateProjectDialogOpen(true);
                }}
                onEditProject={(projectId, name) => {
                  handleProjectClick(projectId, name);
                }}
                onDeleteProject={handleProjectDelete}
                onCreateTeam={() => setIsCreateTeamDialogOpen(true)}
                selectedProjectId={selectedProject?.id}
              />
            </div>
          )}
        </div>

        <div className="nav-center">
          {isReviewDetailView ? (
            reviewTitle ? (
              <div className="review-title">
                {reviewTitle}
              </div>
            ) : (
              <div className="review-title-placeholder"></div>
            )
          ) : (
            <NavMenu />
          )}
        </div>
        
        <div className="nav-right">
          {isReviewDetailView ? (
            <div className="nav-actions">
              <IconTextButton
                variant="secondary"
                size="small"
                icon={addIcon}
                iconAlt="Add key"
                onClick={() => {
                  if (currentReviewId) {
                    const event = new CustomEvent('open-key-dialog', {
                      detail: { reviewId: currentReviewId }
                    });
                    window.dispatchEvent(event);
                  }
                }}
              >
                Add key
              </IconTextButton>
              {isReviewCompleted ? (
                <TextButton
                  variant="secondary"
                  size="small"
                  onClick={onCompleteClick}
                >
                  Re-open review
                </TextButton>
              ) : (
                <IconTextButton
                  variant="secondary"
                  size="small"
                  icon={checkmarkIcon}
                  iconAlt="Done"
                  onClick={onCompleteClick}
                >
                  Mark as done
                </IconTextButton>
              )}
            </div>
          ) : (
            <UserProfile />
          )}
        </div>
      </div>

      {!isReviewDetailView && (
        <div className="toolbar">
          <div className="toolbar-container">
            {location.pathname === '/app/overview' && (
              <>
                <h1 className="toolbar-title">Translation Keys</h1>
                {selectedProject && (
                  <div className="toolbar-actions">
                    <div className="search-container">
                      <img src={searchIcon} alt="Search" className="search-icon" />
                      <input
                        type="text"
                        className="search-input"
                        placeholder="Search keys..."
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          const event = new CustomEvent('update-search-query', {
                            detail: { searchQuery: e.target.value }
                          });
                          window.dispatchEvent(event);
                        }}
                      />
                    </div>
                    {permissions.canManageKeys && (
                      <TextButton
                        variant="secondary"
                        size="small"
                        onClick={handleImportClick}
                        disabled={!selectedProject}
                      >
                        Import
                      </TextButton>
                    )}
                    {permissions.canManageKeys && (
                      <TextButton
                        variant="secondary"
                        size="small"
                        onClick={handleExportClick}
                        disabled={!selectedProject}
                      >
                        Export
                      </TextButton>
                    )}
                    {permissions.canManageKeys && (
                      <IconTextButton
                        variant="primary"
                        size="small"
                        icon={addIcon}
                        iconAlt="Add"
                        onClick={() => {
                          const event = new CustomEvent('open-key-dialog');
                          window.dispatchEvent(event);
                        }}
                      >
                        New
                      </IconTextButton>
                    )}
                  </div>
                )}
              </>
            )}

            {location.pathname === '/app/reviews' && (
              <>
                <h1 className="toolbar-title">Reviews</h1>
                {selectedProject && (
                  <div className="toolbar-actions">
                    <div className="search-container">
                      <img src={searchIcon} alt="Search" className="search-icon" />
                      <input
                        type="text"
                        className="search-input"
                        placeholder="Search reviews..."
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          const event = new CustomEvent('update-search-query', {
                            detail: { searchQuery: e.target.value }
                          });
                          window.dispatchEvent(event);
                        }}
                      />
                    </div>
                    <IconTextButton
                      variant="primary"
                      size="small"
                      icon={addIcon}
                      iconAlt="Add"
                      onClick={() => {
                        const event = new CustomEvent('open-review-dialog');
                        window.dispatchEvent(event);
                      }}
                    >
                      New
                    </IconTextButton>
                  </div>
                )}
              </>
            )}
            
            {location.pathname === '/app/team' && (
              <>
                <h1 className="toolbar-title">Team</h1>
                <div className="toolbar-actions">
                  <div className="search-container">
                    <img src={searchIcon} alt="Search" className="search-icon" />
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search members..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        const event = new CustomEvent('update-search-query', {
                          detail: { searchQuery: e.target.value }
                        });
                        window.dispatchEvent(event);
                      }}
                    />
                  </div>
                  {selectedTeam?.owner_profile_id === profileData?.id && (
                    <IconTextButton
                      variant="secondary"
                      size="small"
                      icon={editIcon}
                      iconAlt="Edit"
                      onClick={() => {
                        const event = new CustomEvent('open-edit-team-dialog');
                        window.dispatchEvent(event);
                      }}
                    >
                      Edit
                    </IconTextButton>
                  )}
                  {permissions.canAddUsers && (
                    <IconTextButton
                      variant="primary"
                      size="small"
                      icon={addIcon}
                      iconAlt="Add"
                      onClick={() => {
                        const event = new CustomEvent('open-invite-dialog');
                        window.dispatchEvent(event);
                      }}
                    >
                      Invite
                    </IconTextButton>
                  )}
                </div>
              </>
            )}
            
            {location.pathname === '/app/tone' && (
              <>
                <h1 className="toolbar-title">Tone of voice</h1>
                <div className="toolbar-actions">
                  <div className="search-container">
                    <img src={searchIcon} alt="Search" className="search-icon" />
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search tone..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        const event = new CustomEvent('update-search-query', {
                          detail: { searchQuery: e.target.value }
                        });
                        window.dispatchEvent(event);
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <CreateProjectDialog
        isOpen={isCreateProjectDialogOpen}
        onClose={() => setIsCreateProjectDialogOpen(false)}
        onSave={handleCreateProject}
        isLoading={creatingProject}
      />

      {projectToEdit && (
        <EditProjectDialog
          isOpen={isEditProjectDialogOpen}
          onClose={() => {
            setIsEditProjectDialogOpen(false);
            setProjectToEdit(null);
          }}
          onSave={handleEditProject}
          onDelete={handleProjectDelete}
          projectName={projectToEdit.name}
          placeholderFormat={projectToEdit.placeholder_format || 'web'}
          isLoading={editingProject}
          projectId={projectToEdit.id}
        />
      )}

      <CreateTeamDialog
        isOpen={isCreateTeamDialogOpen}
        onClose={() => setIsCreateTeamDialogOpen(false)}
        onSave={handleCreateTeam}
        isLoading={creatingTeam}
      />

      <SubscriptionDialog
        isOpen={isSubscriptionDialogOpen}
        onClose={() => setIsSubscriptionDialogOpen(false)}
      />
    </nav>
  );
};

export default TopNavBar; 